import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn, Flex, Grid, Icon, Text } from '@kognitos/lattice';

import {
  clearBrowserPanelFocus,
  consumeBrowserPanelScroll,
  expandBrowserPanelSession,
  focusBrowserPanelSession,
  getBrowserPanelState,
  registerBrowserPanelHost,
  setBrowserPanelOpen,
  setBrowserPanelSlot,
  subscribeToBrowserPanel,
} from '@/modules/outputs/browser-panel-registry';
import { useBrowserSessionLabel } from '@/modules/outputs/hooks';
import { BrowserPreviewStatusBadge } from '@/modules/outputs/components/BrowserPreviewStatus';

import { DraftRailSection } from './DraftRailSection';
import { LIBRARY_ROW_CLASS } from './rail-utils';

/** Eases each session card in as it mounts, instead of popping in. */
const RAIL_CARD_ENTER_CLASS =
  'animate-in fade-in slide-in-from-top-1 duration-300 motion-reduce:animate-none';

/** The block only fades: sliding it too would double the first card's slide. */
const RAIL_BLOCK_ENTER_CLASS =
  'animate-in fade-in duration-300 motion-reduce:animate-none';

/**
 * One chat browser session in the rail's Browser block. The row is the card
 * header: focusing it opens the player below, clicking it again collapses it.
 */
function RailBrowserSessionRow({
  sessionId,
  isFocused,
}: {
  sessionId: string;
  isFocused: boolean;
}) {
  const { t } = useTranslation('browser-automation');
  const label = useBrowserSessionLabel(sessionId);
  const status = useSyncExternalStore(
    subscribeToBrowserPanel,
    () => getBrowserPanelState().sessionStatuses[sessionId] ?? null,
  );

  return (
    <Flex
      direction="column"
      align="stretch"
      width="full"
      className={cn(
        'rounded-xl bg-layout transition-colors has-[[data-rail-browser-header]:hover]:bg-accent',
        RAIL_CARD_ENTER_CLASS,
        isFocused && 'bg-card',
      )}
      data-testid={`draft-rail-browser-card-${sessionId}`}
    >
      {/* Hovering the header tints the whole card; the player area alone doesn't. */}
      <Flex
        align="center"
        gap={1}
        width="full"
        className="group/header pr-1.5"
        data-rail-browser-header
      >
        <Button
          type="button"
          variant="ghost"
          className={cn(
            LIBRARY_ROW_CLASS,
            'flex-1 bg-transparent not-disabled:hover:bg-transparent dark:not-disabled:hover:bg-transparent group-hover/header:text-accent-foreground',
          )}
          aria-current={isFocused || undefined}
          aria-expanded={isFocused}
          onClick={() =>
            isFocused
              ? clearBrowserPanelFocus()
              : focusBrowserPanelSession(sessionId)
          }
          data-testid={`draft-rail-browser-session-${sessionId}`}
        >
          <Icon type="Globe" size="sm" className="shrink-0" />
          <Text level="xSmall" className="min-w-0 truncate text-left">
            {label}
          </Text>
          <BrowserPreviewStatusBadge status={status} />
        </Button>
        {isFocused && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('browserAutomation.action.expand')}
            className="shrink-0 text-muted-foreground"
            onClick={() => expandBrowserPanelSession(sessionId)}
          >
            <Icon type="Maximize" size="xs" />
          </Button>
        )}
      </Flex>
      {/* Kept mounted so the grid row can transition; the player only portals into the focused one. */}
      <Grid
        rows="none"
        className={cn(
          'transition-[grid-template-rows] duration-400 ease-in-out motion-reduce:transition-none',
          isFocused ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            ref={isFocused ? setBrowserPanelSlot : undefined}
            className="px-2 pb-2"
          />
        </div>
      </Grid>
    </Flex>
  );
}

/**
 * Lists the chat's browser sessions. The focused card's player is moved into
 * this block's slot, so collapsing the block stops the stream.
 */
export function RailBrowserSection() {
  const { t } = useTranslation('browser-automation');
  const { sessionIds, focusedSessionId, isOpen, scrollRequested } =
    useSyncExternalStore(subscribeToBrowserPanel, getBrowserPanelState);
  const [sectionEl, setSectionEl] = useState<HTMLDivElement | null>(null);

  // Open from the chat could otherwise expand the block out of sight.
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative DOM API, reacting to an explicit focus request
  useEffect(() => {
    if (!scrollRequested || !sectionEl) return;
    sectionEl.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    consumeBrowserPanelScroll();
  }, [scrollRequested, sectionEl]);

  if (sessionIds.length === 0) return null;

  return (
    <div ref={setSectionEl} className={RAIL_BLOCK_ENTER_CLASS}>
      <DraftRailSection
        title={t('browserAutomation.liveView.title')}
        name="browser"
        count={sessionIds.length}
        open={isOpen}
        onOpenChange={setBrowserPanelOpen}
      >
        {sessionIds.map(id => (
          <RailBrowserSessionRow
            key={id}
            sessionId={id}
            isFocused={id === focusedSessionId}
          />
        ))}
      </DraftRailSection>
    </div>
  );
}

/**
 * The Browser block on its own, for chat layouts without the draft rail.
 * Takes no width until the chat has a session.
 */
export function BrowserSessionsRail() {
  const { sessionIds } = useSyncExternalStore(
    subscribeToBrowserPanel,
    getBrowserPanelState,
  );

  // Held while empty, so the first session doesn't flash inline in the chat.
  // eslint-disable-next-line no-restricted-syntax -- reason: registers in a module registry, with cleanup
  useEffect(() => registerBrowserPanelHost(), []);

  if (sessionIds.length === 0) return null;

  return (
    <Flex
      direction="column"
      align="stretch"
      className="h-full w-[300px] shrink-0 overflow-y-auto py-4 pr-4"
      data-testid="browser-sessions-rail"
    >
      <RailBrowserSection />
    </Flex>
  );
}
